import { Close, Search } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import Fade from 'react-reveal/Fade';
import { useHistory } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import Skeleton from 'react-loading-skeleton';

// Pre-defined fallback categories removed to support dynamic categories

export default function BlogListPage() {
  const { blogs, loading, settings } = useSite();
  const history = useHistory();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 8; // 2 rows of 4

  // Dynamic Categories derived from content
  const categories = useMemo(() => {
    const blogCats = (blogs || []).map(b => b.category).filter(Boolean);
    const uniqueCats = Array.from(new Set(blogCats));
    return ['All', ...uniqueCats.sort()];
  }, [blogs]);

  const processedPosts = useMemo(() => {
    // 1. Filter
    let filtered = (blogs || []).filter((p) => {
      const matchesCat =
        activeCategory === 'All' || p.category === activeCategory;
      const matchesSearch =
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });

    // 2. Sort: Featured first (isFeatured), then by date
    return filtered.sort((a, b) => {
      const aFeat = a.isFeatured || a.featured;
      const bFeat = b.isFeatured || b.featured;
      if (aFeat && !bFeat) return -1;
      if (!aFeat && bFeat) return 1;
      return new Date(b.date) - new Date(a.date);
    });
  }, [activeCategory, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(processedPosts.length / postsPerPage);
  const currentPosts = processedPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClear = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  return (
    <div className="waya-blog-listing-wrapper">
      {/* Spacer to fix navbar overlap */}
      <div style={{ height: '100px' }}></div>
      <div className="container mt-5">
        <Fade bottom>
          <div className="text-center mb-5">
            <h6 className="waya-section-badge mx-auto">Latest Updates</h6>
            <h1 className="waya-section-title mt-3">{settings?.site_name || 'WayaBank'} Insights</h1>
            <p className="waya-section-subtitle mx-auto">
              Stay informed with the latest news, financial tips, and ecosystem
              updates.
            </p>
          </div>

          {/* Premium Search Bar */}
          <div className="waya-blog-controls text-center mb-5">
            <div
              className="waya-search-wrapper mx-auto mb-5"
              style={{ maxWidth: '700px' }}
            >
              <div
                className="waya-search-container-premium shadow-sm"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'white',
                  borderRadius: '16px',
                  padding: '6px',
                  border: '1px solid #eaecf0',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ padding: '0 15px', color: '#667085' }}>
                  <Search fontSize="medium" />
                </div>
                <input
                  type="text"
                  placeholder="Search for articles, news, or tips..."
                  className="waya-premium-input"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px',
                    padding: '12px 0',
                    color: '#101828',
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={handleClear}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '10px',
                      color: '#98a2b3',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Close fontSize="small" />
                  </button>
                )}
                <button
                  className="waya-btn-search-premium"
                  style={{
                    background: 'var(--waya-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '10px 24px',
                    fontWeight: 600,
                    marginLeft: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  Search
                </button>
              </div>
            </div>

            <div className="waya-filter-bar">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`filter-pill ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(cat);
                    setCurrentPage(1);
                  }}
                    style={{
                      backgroundColor:
                        activeCategory === cat ? 'var(--waya-primary)' : 'white',
                      color: activeCategory === cat ? '#FFFFFF' : '#475467',
                    }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </Fade>

        {/* Blog Grid (4 Columns) */}
        <div className="waya-blog-grid">
          {loading ? (
            [...Array(postsPerPage)].map((_, idx) => (
              <div key={idx} className="waya-blog-item shadow-sm">
                <Skeleton height={200} style={{ borderRadius: '16px 16px 0 0' }} />
                <div className="blog-item-body">
                   <div className="d-flex justify-content-between mb-2">
                     <Skeleton width={60} />
                     <Skeleton width={80} />
                   </div>
                   <Skeleton height={24} width="90%" className="mb-2" />
                   <Skeleton count={2} />
                   <div className="mt-3 d-flex justify-content-between">
                     <Skeleton width={50} />
                     <Skeleton width={70} />
                   </div>
                </div>
              </div>
            ))
          ) : (
            currentPosts.map((post, idx) => (
              <Fade bottom delay={idx * 50} key={post.id}>
                <div
                  className="waya-blog-item clickable shadow-sm"
                  onClick={() => history.push(`/blog/${post.id}`)}
                  style={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {post.isFeatured && (
                    <div className="featured-badge-overlay">FEATURED</div>
                  )}
                  <div
                    className="blog-item-img-container"
                    style={{ overflow: 'hidden', height: '200px' }}
                  >
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="blog-item-img"
                      style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <div className="blog-item-body">
                    <div className="blog-item-meta">
                      <span className="blog-item-cat">{post.category}</span>
                      <span className="blog-item-date">{post.date}</span>
                    </div>
                    <h3 className="blog-item-title" style={{ fontSize: '18px' }}>
                      {post.title}
                    </h3>
                    <p
                      className="blog-excerpt mb-3"
                      style={{ fontSize: '14px', color: '#475467' }}
                    >
                      {post.excerpt.length > 80
                        ? post.excerpt.substring(0, 80) + '...'
                        : post.excerpt}
                    </p>
                    <div className="mt-auto d-flex align-items-center justify-content-between">
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#667085',
                          fontWeight: 600,
                        }}
                      >
                        {post.readTime}
                      </span>
                      <div
                        className="text-primary fw-bold"
                        style={{ fontSize: '12px' }}
                      >
                        Read More →
                      </div>
                    </div>
                  </div>
                </div>
              </Fade>
            ))
          )}
        </div>

        {/* Empty State */}
        {currentPosts.length === 0 && (
          <div className="text-center py-5">
            <h3 className="text-muted">
              No articles found matching your criteria.
            </h3>
          </div>
        )}

        {/* Numeric Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-5 gap-2">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`waya-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  border: '1px solid #eaecf0',
                  // Fixing contrast: text is white on theme color when active
                  background:
                    currentPage === i + 1 ? 'var(--waya-primary)' : 'white',
                  color: currentPage === i + 1 ? '#FFFFFF' : '#475467',
                  fontWeight: 800,
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow:
                    currentPage === i + 1
                      ? '0 4px 12px rgba(255,68,0,0.2)'
                      : 'none',
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
